/* eslint-disable @typescript-eslint/no-unsafe-type-assertion */
/* eslint-disable @typescript-eslint/class-methods-use-this -- Methods are part of service interface */
/**
 * Minimal Helia Service for Browser
 *
 * Stripped down to essentials:
 * - In-memory blockstore (no IndexedDB complexity)
 * - Bitswap for P2P block exchange
 * - UnixFS for file operations
 */
import { createHelia, type Helia } from 'helia';
import { unixfs, type UnixFS } from '@helia/unixfs';
import { MemoryBlockstore } from 'blockstore-core';
import { bitswap } from '@helia/block-brokers';
import { fixedSize } from 'ipfs-unixfs-importer/chunker'
import { IDBBlockstore } from 'blockstore-idb';
import { IDBDatastore } from 'datastore-idb';

import { P2P_CONFIG, CHUNK_SIZE } from '@/config/p2pConfig';
import { createLogger } from '@/utils/logger';

import type { MinimalLibp2p } from './libp2p-minimal.service';

const log = createLogger({ namespace: P2P_CONFIG.logging.namespace.helia });

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface MinimalHeliaNode {
  helia: Helia;
  fs: UnixFS;
}

export interface FileUploadResult {
  cid: string;
  size: number;
  name: string;
}

/* -------------------------------------------------------------------------- */
/*  Implementation                                                             */
/* -------------------------------------------------------------------------- */

export class MinimalHeliaService {
  private blocksStored = 0;
  private bytesStored = 0;
  private statsInterval: ReturnType<typeof setInterval> | null = null;
  private lastBlockCount = 0;

  private getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Create a minimal Helia node attached to an existing libp2p node.
   */
  async createNode(libp2p: MinimalLibp2p): Promise<MinimalHeliaNode> {
    log.info('Creating browser Helia node');

          const blockstore = new IDBBlockstore(`minimal-helia-blockstore`);
          const datastore = new IDBDatastore(`minimal-helia-datastore`);
          // Open stores
          await Promise.all([
            blockstore.open(),
            datastore.open()
          ]);
          log.debug('IDB stores opened');
          // Store references for proper cleanup later
          // Create Helia node with bitswap for P2P block exchange

    // Configure Bitswap with extended timeouts for slow networks
    const bitswapConfig = {
      // Reduce parallelism to prevent congestion collapse
      maxInboundStreams: P2P_CONFIG.bitswap.maxInboundStreams,      // Down from 32
      maxOutboundStreams: P2P_CONFIG.bitswap.maxOutboundStreams,    // Down from 128 - KEY SETTING
      messageSendConcurrency: P2P_CONFIG.bitswap.messageSendConcurrency, // Down from 50 - controls WANT parallelism
      sendBlocksConcurrency: P2P_CONFIG.bitswap.sendBlocksConcurrency,  // Down from 50
      sendBlocksDebounce: P2P_CONFIG.bitswap.sendBlocksDebounce,    // Up from 10ms - adds breathing room

      // CRITICAL: Increase all timeouts to survive slow networks
      incomingStreamTimeout: P2P_CONFIG.bitswap.incomingStreamTimeout,  // 2 minutes - prevent premature stream closure
      outgoingStreamTimeout: P2P_CONFIG.bitswap.outgoingStreamTimeout,  // 2 minutes - match incoming
      sendBlocksTimeout: P2P_CONFIG.bitswap.sendBlocksTimeout,       // 1 minute - time to send a block
      receiveBlocksTimeout: P2P_CONFIG.bitswap.receiveBlocksTimeout,   // 2 minutes - time to receive blocks
      streamIdleTimeout: P2P_CONFIG.bitswap.streamIdleTimeout       // 3 minutes - keep streams alive during slow transfers
    };

    log.debug('Bitswap config', { config: bitswapConfig });

    const helia = await createHelia({
      libp2p,
      blockstore,
      blockBrokers: [bitswap(bitswapConfig)]
    });

    const fs = unixfs(helia);

    log.info('Helia node created');

    // Start periodic blockstore stats logging
    this.startBlockstoreLogging(helia);

    // Set up Bitswap protocol logging
    this.setupBitswapLogging(libp2p);

    return { helia, fs };
  }

  /**
   * Set up logging for Bitswap protocol streams.
   * This logs when peers have active Bitswap streams (indicating block transfers).
   */
  private setupBitswapLogging(libp2p: MinimalLibp2p): void {
    log.debug('Setting up Bitswap stream logging');

    let lastBitswapStreamCount = 0;
    let lastInboundCount = 0;
    let lastOutboundCount = 0;

    // Poll connections for stream info periodically
    setInterval(() => {
      const connections = libp2p.getConnections();
      let bitswapStreams = 0;
      let inboundBitswap = 0;
      let outboundBitswap = 0;
      const peersWithBitswap: string[] = [];

      for (const conn of connections) {
        const streams = conn.streams;
        let connHasBitswap = false;

        for (const stream of streams) {
          if (stream.protocol.startsWith(P2P_CONFIG.bitswap.protocolPrefix)) {
            bitswapStreams++;
            connHasBitswap = true;
            if (stream.direction === 'inbound') {
              inboundBitswap++;
            } else {
              outboundBitswap++;
            }
          }
        }

        if (connHasBitswap) {
          peersWithBitswap.push(conn.remotePeer.toString().substring(0, P2P_CONFIG.logging.peerIdLogLength) + '...');
        }
      }

      // Log if bitswap activity changed
      if (bitswapStreams !== lastBitswapStreamCount ||
          inboundBitswap !== lastInboundCount ||
          outboundBitswap !== lastOutboundCount) {

        if (bitswapStreams > 0) {
          log.debug('Bitswap streams active', {
            total: bitswapStreams,
            inbound: inboundBitswap,
            outbound: outboundBitswap,
            peers: peersWithBitswap,
            note: inboundBitswap > 0 ? 'Peers requesting blocks FROM you' : 'You requesting blocks from peers'
          });
        } else if (lastBitswapStreamCount > 0) {
          log.debug('Bitswap streams: All closed (transfer complete)');
        }

        lastBitswapStreamCount = bitswapStreams;
        lastInboundCount = inboundBitswap;
        lastOutboundCount = outboundBitswap;
      }
    }, P2P_CONFIG.logging.statsLogInterval);
  }

  /**
   * Start periodic logging of blockstore statistics.
   * This helps track when blocks are being served to other peers.
   */
  private startBlockstoreLogging(helia: Helia): void {
    log.debug('Starting blockstore stats logging');

    this.statsInterval = setInterval(() => {
      void this.logBlockstoreStats(helia);
    }, P2P_CONFIG.logging.statsLogInterval);
  }

  /**
   * Log current blockstore statistics.
   */
  private async logBlockstoreStats(helia: Helia): Promise<void> {
    try {
      let blockCount = 0;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Iterating to count blocks
      for await (const _ of helia.blockstore.getAll()) {
        blockCount++;
      }

      const blocksDelta = blockCount - this.lastBlockCount;

      if (blocksDelta !== 0) {
        log.debug('Blockstore changed', {
          blocks: blockCount,
          blocksDelta: blocksDelta > 0 ? `+${blocksDelta}` : blocksDelta,
          note: blocksDelta > 0 ? 'Blocks added (upload or received)' : 'Blocks removed'
        });
      }

      this.lastBlockCount = blockCount;
    } catch {
      // Silently ignore errors during stats collection
    }
  }

  /**
   * Get current blockstore block count on demand.
   */
  async getBlockstoreStats(node: MinimalHeliaNode): Promise<{ blocks: number }> {
    let blockCount = 0;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Iterating to count blocks
    for await (const _ of node.helia.blockstore.getAll()) {
      blockCount++;
    }

    return { blocks: blockCount };
  }

  /**
   * Stop the Helia node.
   */
  async stopNode(node: MinimalHeliaNode): Promise<void> {
    log.info('Stopping Helia node');

    // Stop stats logging
    if (this.statsInterval !== null) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
      log.debug('Stopped blockstore stats logging');
    }

    await node.helia.stop();
    log.info('Helia node stopped');
  }

  /**
   * Add a file to Helia and return the CID.
   */
  async addFile(node: MinimalHeliaNode, file: File): Promise<FileUploadResult> {
    log.info('Upload started', { name: file.name, size: file.size });

    this.blocksStored = 0;
    this.bytesStored = 0;
    const addStart = Date.now();

    // ---------------------------------------------------------
    // CRITICAL FIX 1: Use Streams, NOT ArrayBuffer
    // ---------------------------------------------------------
    // Old (Bad): const arrayBuffer = await file.arrayBuffer();
    // Old (Bad): const bytes = new Uint8Array(arrayBuffer);

    // New (Good): Get the stream directly from the File object
    const fileIterator = streamToIterator(file.stream());
    // ---------------------------------------------------------
    // CRITICAL FIX 2: Use addByteStream + 256kb Chunks
    // ---------------------------------------------------------
    const cid = await node.fs.addByteStream(fileIterator, {
      // Standard IPFS block size.
      // Keeps the WebSocket messages small so Pings can get through.
      chunker: fixedSize({ chunkSize: CHUNK_SIZE }),

      onProgress: (evt) => {
        if (evt.type === 'unixfs:importer:progress:file:write') {
          this.blocksStored++;
          // bytesWritten is the size of the chunk, usually ~262144
          const written = Number(evt.detail.bytesWritten);
          this.bytesStored += written;

          // Only log every 50 blocks to reduce console lag
          if (this.blocksStored % 50 === 0) {
             const elapsed = (Date.now() - addStart) / 1000;
             const mb = (this.bytesStored / 1024 / 1024).toFixed(2);
             log.debug('Import progress', { mb, blocks: this.blocksStored, elapsedSeconds: elapsed });
          }
        }
      }
    });

    const addTime = Date.now() - addStart;
    log.info('Upload complete', { cid: cid.toString(), timeMs: addTime });

    return {
      cid: cid.toString(),
      size: file.size,
      name: file.name
    };
  }

/**
   * Get a file from Helia by CID string with aggressive backpressure.
   * Optimized for private networks to prevent Yamux window congestion.
   */
  async getFile(node: MinimalHeliaNode, cidString: string): Promise<Blob> {
    const shortCid = cidString.substring(0, 20) + '...';
    log.info('Download started', { shortCid, fullCid: cidString });

    // Stage 1: Parse CID
    log.debug('STAGE 1/5: Parsing CID');
    const parseStart = Date.now();
    const { CID } = await import('multiformats/cid');
    const cid = CID.parse(cidString);
    const parseTime = Date.now() - parseStart;
    log.debug('STAGE 1/5: CID parsed', { timeMs: parseTime });

    // Stage 2: Initialize download
    log.debug('STAGE 2/5: Initializing download stream');

    // Check if we have the CID locally first
    const hasLocal = await node.helia.blockstore.has(cid);
    log.debug('STAGE 2/5: Local blockstore check', { hasLocal, note: hasLocal ? 'CID EXISTS locally' : 'CID NOT found locally - will fetch from peers' });

    // Check peer connectivity
    const connections = node.helia.libp2p.getConnections();
    log.debug('STAGE 2/5: Connected peers', { count: connections.length });
    if (connections.length === 0) {
      log.warn('No peers connected! Cannot fetch blocks.');
    } else {
      log.debug('STAGE 2/5: Peers', { peers: connections.map(c => c.remotePeer.toString().substring(0, P2P_CONFIG.logging.peerIdLogLength) + '...') });
    }

    let chunkCount = 0;
    let totalBytes = 0;
    const startTime = Date.now();
    const chunks: Uint8Array[] = [];

    try {
      // Pass { session: false } to reduce CPU overhead of session management
      // This forces a broadcast "want" to all connected peers
      const catOptions = { session: false };
      log.debug('STAGE 2/5: Stream options configured', { session: false });

      // Stage 3: Download chunks
      log.debug('STAGE 3/5: Starting chunk download');
      log.debug('STAGE 3/5: Waiting for first chunk from Bitswap');
      const downloadStart = Date.now();

      for await (const chunk of node.fs.cat(cid, catOptions as any)) {
        chunkCount++;
        totalBytes += chunk.length;
        chunks.push(chunk);

        // Log first chunk immediately
        if (chunkCount === 1) {
          const firstChunkTime = Date.now() - downloadStart;
          log.debug('STAGE 3/5: First chunk received', { size: chunk.length, timeMs: firstChunkTime });
        }

        // Log every chunk for the first 5 chunks
        if (chunkCount <= 5) {
          const elapsed = Date.now() - downloadStart;
          log.debug('Chunk received', { chunkNumber: chunkCount, size: chunk.length, totalBytes, elapsedMs: elapsed });
        }

        // Periodic logging every 1MB (assuming 256kb chunks = every 4 chunks)
        if (chunkCount > 5 && chunkCount % 4 === 0) {
           const elapsed = Date.now() - downloadStart;
           const mb = (totalBytes / 1024 / 1024).toFixed(2);
           const speed = totalBytes / (elapsed / 1000);
           const speedMBps = (speed / 1024 / 1024).toFixed(2);
           log.debug('Download progress', { mb, chunks: chunkCount, speedMBps });
        }
      }

      const downloadTime = Date.now() - downloadStart;
      log.debug('STAGE 3/5: All chunks received', { chunks: chunkCount, bytes: totalBytes, timeMs: downloadTime });

      // Stage 4: Create Blob
      log.debug('STAGE 4/5: Creating Blob', { chunkCount });
      const blobStart = Date.now();
      const blob = new Blob(chunks as unknown as BlobPart[], { type: 'application/octet-stream' });
      const blobTime = Date.now() - blobStart;
      log.debug('STAGE 4/5: Blob created', { size: blob.size, timeMs: blobTime });

      // Stage 5: Complete
      const totalElapsed = Date.now() - startTime;
      const sizeMB = (totalBytes / 1024 / 1024).toFixed(2);
      const avgSpeed = totalBytes / (totalElapsed / 1000);
      const avgSpeedMBps = (avgSpeed / 1024 / 1024).toFixed(2);
      log.info('STAGE 5/5: Download complete', { shortCid, sizeMB, chunks: chunkCount, totalTimeMs: totalElapsed, avgSpeedMBps });

      return blob;

    } catch (error) {
      const failedElapsed = Date.now() - startTime;
      const partialMB = (totalBytes / 1024 / 1024).toFixed(2);
      log.error('Download failed', { shortCid, chunksReceived: chunkCount, bytes: totalBytes, partialMB, timeMs: failedElapsed });
      log.error('Download error', error);
      throw error;
    }
  }

/**
   * Check if a CID exists in local blockstore.
   */
  async hasBlock(node: MinimalHeliaNode, cidString: string): Promise<boolean> {
    const { CID } = await import('multiformats/cid');
    const cid = CID.parse(cidString);
    return await node.helia.blockstore.has(cid);
  }
}

/**
 * Helper: Convert Browser ReadableStream to AsyncIterable
 */
async function* streamToIterator(stream: ReadableStream<Uint8Array>): AsyncIterable<Uint8Array> {
  const reader = stream.getReader();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      yield value;
    }
  } finally {
    reader.releaseLock();
  }
}
// Export singleton for convenience
export const minimalHeliaService = new MinimalHeliaService();
